import React, { useState } from 'react';
import { ScriptScene } from '@/api';

interface StoryboardPreviewProps {
  scenes: ScriptScene[];
  projectTitle: string;
  onSelectStyle?: (style: 'biblical' | 'african' | 'modern') => void;
}

export default function StoryboardPreview({ scenes, projectTitle, onSelectStyle }: StoryboardPreviewProps) {
  const [selectedStyle, setSelectedStyle] = useState<'biblical' | 'african' | 'modern' | null>(null);

  if (!scenes || scenes.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8">
        <div className="text-center py-12">
          <div className="w-24 h-24 bg-gradient-to-r from-yellow-100 to-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-12 h-12 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Scenes Generated</h3>
          <p className="text-gray-600 mb-6">This script was generated before scene support. Regenerate to create a visual storyboard.</p>
          
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium"
          >
            Regenerate Script with Scenes
          </button>
        </div>
      </div>
    );
  }

  const totalDuration = scenes.reduce((sum, scene) => {
    const [start, end] = scene.timestamp.match(/\d+/g) || ['0', '0'];
    return sum + (parseInt(end) - parseInt(start));
  }, 0);

  return (
    <div className="space-y-6">
      {/* Style Selector */}
      {!selectedStyle && (
        <div className="bg-white rounded-xl border border-gray-200 p-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Choose Visual Style</h3>
          <p className="text-gray-600 mb-6">Select the visual style for your scenes. This will be used when generating images.</p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              {
                id: 'biblical',
                title: '📖 Biblical Renaissance',
                description: 'Classic oil painting style, dramatic lighting, religious art',
                color: 'from-amber-100 to-yellow-100'
              },
              {
                id: 'african',
                title: '🌍 African Traditional',
                description: 'Vibrant colors, cultural patterns, warm earth tones',
                color: 'from-orange-100 to-red-100'
              },
              {
                id: 'modern',
                title: '✨ Modern Illustration',
                description: 'Digital art, cinematic lighting, vibrant colors',
                color: 'from-blue-100 to-purple-100'
              }
            ].map((style) => (
              <button
                key={style.id}
                onClick={() => {
                  setSelectedStyle(style.id as any);
                  onSelectStyle?.(style.id as any);
                }}
                className={`p-6 rounded-xl border-2 border-gray-200 hover:border-indigo-500 transition-all text-left bg-gradient-to-br ${style.color}`}
              >
                <h4 className="font-semibold text-gray-900 mb-2">{style.title}</h4>
                <p className="text-sm text-gray-600">{style.description}</p>
                <div className="mt-4 text-xs font-medium text-gray-500">
                  Cost: ~$0.40 for {scenes.length} scenes
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Storyboard Display */}
      {selectedStyle && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">
              Storyboard - {projectTitle}
            </h3>
            <div className="text-sm text-gray-600">
              <span className="font-medium">{scenes.length} scenes</span> • 
              <span className="font-medium ml-1">{totalDuration}s total</span>
            </div>
          </div>

          <div className="space-y-3">
            {scenes.map((scene, index) => (
              <div key={index} className="bg-white rounded-lg border border-gray-200 p-4 hover:border-indigo-400 transition-colors">
                <div className="flex gap-4">
                  {/* Scene Image */}
                  <div className="flex-shrink-0">
                    <div className="w-32 h-20 bg-gradient-to-br from-gray-200 to-gray-300 rounded-lg flex items-center justify-center overflow-hidden">
                      {scene.imageUrl ? (
                        <img src={scene.imageUrl} alt={scene.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="text-center">
                          <div className="text-2xl">🖼️</div>
                          <div className="text-xs text-gray-500 mt-1">Image</div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Scene Content */}
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h4 className="font-semibold text-gray-900">
                          Scene {scene.sceneNumber}: {scene.title}
                        </h4>
                        <p className="text-xs text-gray-500 mt-1">
                          {scene.timestamp} • {scene.mood}
                        </p>
                      </div>
                      <div className="text-xs bg-indigo-100 text-indigo-700 px-2 py-1 rounded font-medium">
                        {scene.suggestedAnimation}
                      </div>
                    </div>

                    <p className="text-sm text-gray-700 mb-2">
                      <span className="font-medium text-gray-900">Visual:</span> {scene.visualDescription}
                    </p>

                    <div className="flex items-center gap-4 text-xs text-gray-600">
                      {scene.characters.length > 0 && (
                        <div>
                          <span className="font-medium">Characters:</span> {scene.characters.join(', ')}
                        </div>
                      )}
                      {scene.setting && (
                        <div>
                          <span className="font-medium">Setting:</span> {scene.setting}
                        </div>
                      )}
                    </div>

                    <p className="text-sm text-gray-700 mt-2 italic">
                      "{scene.scriptContent}"
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              onClick={() => setSelectedStyle(null)}
              className="flex-1 px-4 py-3 bg-gray-100 text-gray-900 rounded-lg hover:bg-gray-200 transition-colors font-medium"
            >
              ← Change Style
            </button>
            <button
              className="flex-1 px-4 py-3 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors font-medium"
            >
              Generate Images → $0.40
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
